import { DomainEntityDetail, TreeNode } from '@web-config-app/core';
import { getBreadcrumbsForProperty } from '@web-config-app/core-react';
import jp from 'jsonpath';
import { getPropertyNameFromPath } from '../get-property-name-from-path/get-property-name-from-path';

/**
 * Generates move targets from a JSON path that contains array wildcards
 * @param jsonPath - JSON path like "$.contentData.page.[*].components"
 * @param entityRootData - The root data object to extract array information from
 * @param nodeMap - Entity tree node map for breadcrumb generation
 * @param translate - Translation function
 * @param moveFromPath - The path of the item being moved from
 * @returns Array of breadcrumb strings for each array item and its target path
 */
export const generateMoveTargetsFromJsonPath = (
  jsonPath: string,
  entityRootData: Partial<DomainEntityDetail> | undefined,
  nodeMap: Map<string, TreeNode>,
  translate: (args: { id: string }) => string,
  moveFromPath?: string,
): { breadcrumb: string; targetPath: string }[] => {
  // Early return if we don't have valid entity data
  const domainEntityAttributes =
    entityRootData?.attributes?.domainEntityAttributes;
  if (!domainEntityAttributes || typeof domainEntityAttributes !== 'object') {
    return [];
  }

  // Get the array data from the entityRootData to get the array length
  const arrayData = jp.value(domainEntityAttributes, jsonPath);

  const propertyName = getPropertyNameFromPath(moveFromPath);
  if (!Array.isArray(arrayData) || !propertyName) {
    return [];
  }

  // Generate targets for each array item
  return arrayData
    .map((_, index) => {
      const targetPath = `${jsonPath}.${index}.${propertyName}`.replace(
        /^\$\./,
        '',
      );

      // If the target path is the same as the move from path, skip it. User should not be able to move an item to the same location.
      if (targetPath === moveFromPath) {
        return null;
      }

      // Generate breadcrumbs for this target path
      const breadcrumbs = getBreadcrumbsForProperty({
        path: targetPath,
        data: entityRootData,
        entityTreeNodeMap: nodeMap,
        translate,
      });

      return {
        breadcrumb: breadcrumbs
          .map(({ label: breadcrumb }) => breadcrumb)
          .join(' → '),
        targetPath,
      };
    })
    .filter(
      (breadcrumb): breadcrumb is { breadcrumb: string; targetPath: string } =>
        breadcrumb !== null,
    );
};
